import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";

import { TreemapMeasure } from "../../../types/analytics";
import { TreemapColour } from "../analyticsState";
import {
  DOMESTIC_OPACITY,
  TINT_OPACITY,
  hatchOf,
  tintFill,
} from "./TreemapCanvas";

const Swatch: React.FC<{ fill?: string; image?: string; dashed?: boolean }> = ({
  fill,
  image,
  dashed = false,
}) => (
  <Box
    component="span"
    aria-hidden
    style={{ backgroundColor: fill }}
    sx={{
      backgroundImage: image ?? "none",
      display: "inline-block",
      width: 18,
      height: 12,
      mr: "5px",
      borderRadius: "3px",
      verticalAlign: "middle",
      boxSizing: "border-box",
      border: "1px solid",
      borderStyle: dashed ? "dashed" : "solid",
      borderColor: dashed ? (theme) => alpha(theme.palette.chart.ink, 0.16) : "divider",
    }}
  />
);

// What the area and the scale say under each measure and colour mode
const AREA_WORDS: Record<TreemapMeasure, string> = {
  products: "products on record",
  makers: "makers on record",
  articles: "articles",
};
const SCALE_WORDS: Record<TreemapColour, [string, string, string]> = {
  products: ["products on record", "fewer", "more"],
  makers: ["makers on record", "fewer", "more"],
  domestic: ["domestic share", "less domestic", "more domestic"],
};

interface TreemapLegendProps {
  measure: TreemapMeasure;
  colour: TreemapColour;
  // Whether the classes with nothing on record are drawn
  gaps: boolean;
}

// What the area and the colour say, the five tints from few to many, then
// the marks a tile can carry
export const TreemapLegend: React.FC<TreemapLegendProps> = ({ measure, colour, gaps }) => {
  const theme = useTheme();
  const [mode, low, high] = SCALE_WORDS[colour];
  const tints = colour === "domestic" ? DOMESTIC_OPACITY : TINT_OPACITY;
  const hatch = hatchOf(theme);
  return (
    <Box
      role="list"
      aria-label="Legend"
      sx={{
        display: "flex",
        columnGap: 2,
        rowGap: 0.5,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      <Box component="span" role="listitem">
        area = {AREA_WORDS[measure]} · colour = {mode}
      </Box>
      <Box component="span" role="listitem">
        <Box component="span" sx={{ mr: "6px" }}>
          {low}
        </Box>
        {tints.map((_opacity, tint) => (
          <Swatch key={tint} fill={tintFill(theme, colour, tint)} />
        ))}
        {high}
      </Box>
      {colour === "domestic" && (
        <Box component="span" role="listitem">
          <Swatch fill={tintFill(theme, colour, null)} />
          no share to show
        </Box>
      )}
      <Box component="span" role="listitem">
        <Swatch image={hatch} />
        under the floor, or filed under no class
      </Box>
      {gaps && (
        <Box component="span" role="listitem">
          <Swatch image={hatch} dashed />
          gap: nothing on record
        </Box>
      )}
    </Box>
  );
};

export default TreemapLegend;
